import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className
}) => {
  if (totalPages <= 1) return null;

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 1; // Number of pages to show on each side of current page
    
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // Always show first page
        i === totalPages || // Always show last page
        (i >= currentPage - delta && i <= currentPage + delta) // Show pages around current
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    
    return pages;
  };

  const pages = generatePageNumbers();

  return (
    <div className={cn("flex items-center justify-center gap-2", className)}>
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={cn(
          "w-10 h-10 flex items-center justify-center border transition-all duration-300",
          "uppercase text-[10px] font-bold tracking-wider",
          currentPage === 1
            ? "border-white/5 text-white/10 cursor-not-allowed"
            : "border-white/10 text-white/40 hover:border-primary hover:text-primary hover:bg-primary/5"
        )}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>

      {/* Page Numbers */}
      {pages.map((page, index) => {
        if (page === '...') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="w-10 h-10 flex items-center justify-center text-white/20 text-xs font-bold"
            >
              ...
            </span>
          );
        }

        const pageNum = page as number;
        const isActive = pageNum === currentPage;

        return (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={cn(
              "w-10 h-10 flex items-center justify-center border transition-all duration-300",
              "uppercase text-[10px] font-bold tracking-wider",
              isActive
                ? "border-primary bg-primary/10 text-primary shadow-[0_0_10px_rgba(176,138,90,0.3)]"
                : "border-white/10 text-white/40 hover:border-primary hover:text-primary hover:bg-primary/5"
            )}
            aria-label={`Page ${pageNum}`}
            aria-current={isActive ? 'page' : undefined}
          >
            {pageNum}
          </button>
        );
      })}

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={cn(
          "w-10 h-10 flex items-center justify-center border transition-all duration-300",
          "uppercase text-[10px] font-bold tracking-wider",
          currentPage === totalPages
            ? "border-white/5 text-white/10 cursor-not-allowed"
            : "border-white/10 text-white/40 hover:border-primary hover:text-primary hover:bg-primary/5"
        )}
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};
